import { AutoAwesomeOutlined, ExpandLess } from '@mui/icons-material'
import { BEIGE_600, BOX_SHADOW_XS, CORAL_600, MOBILE_VIEW_QUERY, WHITE } from 'constants/styling/theme'
import { GenerateTextFlowStep, useVisualsMarketing } from 'components/pages/VisualsMarketing/_main/contexts'
import { IconButton, SwipeableDrawer, useMediaQuery } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'

import Box from '@mui/material/Box'
import { EmptyContent } from 'components/common/EmptyContent'
import { Loading } from 'components/pages/VisualsMarketing/Loading'
import { PropertyIntroduction } from 'components/pages/VisualsMarketing/PropertyIntroduction'
import Stack from '@mui/material/Stack'
import { VisualsMarketingSidebar } from 'components/pages/VisualsMarketing/VisualsMarketingSidebar'
import { VisualsMarketingSocialMedia } from 'components/pages/VisualsMarketing/SocialMedia'
import { useTranslation } from 'react-i18next'

/**
 * Client Property AI text generator content.
 *
 * @example <ClientPropertyAITextGenerator />
 */
export const ClientPropertyAITextGenerator: React.FC = () => {

  const { t } = useTranslation(['gallery_marketing_section', 'visual_marketing_property_form'])

  const { generateTextStep, setIsPropertyDetailsEditing } = useVisualsMarketing()

  const isMobile = useMediaQuery(MOBILE_VIEW_QUERY)

  const [isDrawerOpened, setIsDrawerOpened] = useState(false)

  const handleMobileClose = useCallback(() => {
    setIsDrawerOpened(false)
    setIsPropertyDetailsEditing(false)
  }, [setIsPropertyDetailsEditing])

  useEffect(() => {
    if (!isMobile) setIsDrawerOpened(false)
  }, [isMobile])

  return (
    <Stack direction="row" gap="2rem" width="100%">

      {/* SIDEBAR */}
      {!isMobile &&
        <Box
          sx={{
            marginTop: '3.2rem',
            maxHeight: 'calc(65vh - 3.2rem)',
            minWidth: '250px',
            maxWidth: '350px', // value from marketingSidebar
            borderRadius: '8px',
            boxSizing: 'border-box',
            backgroundColor: WHITE,
            border: `1px solid ${BEIGE_600}`,
            boxShadow: BOX_SHADOW_XS,
          }}
        >
          <VisualsMarketingSidebar
            isMobile={isMobile}
            onClose={isMobile ? handleMobileClose : undefined}
          />
        </Box>
      }

      <Stack
        sx={{
          width: '100%',
          marginLeft: 'auto',
          paddingTop: '3.2rem',
          boxSizing: 'border-box',
          maxWidth: `${!isMobile ? '65%' : '100%'}`,
          paddingBottom: `${!isMobile ? '3.2rem' : '9.2rem'}`, // to make space for the bottom bar
        }}
      >

        {/** LOADING / EMPTY CONTENT */}
        {(
          generateTextStep === GenerateTextFlowStep.EMPTY ||
          generateTextStep === GenerateTextFlowStep.LOADING
        ) &&
          <Stack height="100%" justifyContent="center">

            {/** EMPTY STEP */}
            {generateTextStep === GenerateTextFlowStep.EMPTY &&
              <Box maxWidth="40rem" width="100%" margin="auto">
                <EmptyContent
                  icon={<AutoAwesomeOutlined sx={{ fontSize: 32, color: CORAL_600 }} />}
                  title={t('gallery_marketing_section:empty.title')}
                  description={t('gallery_marketing_section:empty.description')}
                />
              </Box>
            }

            {/** LOADING */}
            {generateTextStep === GenerateTextFlowStep.LOADING &&
              <Box maxWidth="40rem" width="100%" margin="auto">
                <Loading />
              </Box>
            }

          </Stack>
        }

        {/** GENERATED TEXT SECTION */}
        {generateTextStep === GenerateTextFlowStep.TEXT_EXISTS &&
          <Stack gap="2rem">
            {/** PROPERTY DETAILS */}
            <PropertyIntroduction />

            {/** SOCIAL MEDIA */}
            <VisualsMarketingSocialMedia />
          </Stack>
        }

      </Stack>

      {isMobile &&
        <Box position="fixed" left="0" right="0" bottom="0" width="100%">

          <Stack
            boxSizing="border-box"
            bgcolor={WHITE}
            borderRadius="24px 24px 0 0"
            width="100%"
            height="6rem"
            boxShadow="0 0 20px 0 rgba(0, 0, 0, 0.07)"
            direction="row"
            alignItems="center"
            padding="0 2rem"
            justifyContent="space-between"
          >
            <h2>{t('visual_marketing_property_form:property_info')}</h2>
            <IconButton onClick={() => setIsDrawerOpened(true)}>
              <ExpandLess fontSize="large" />
            </IconButton>
          </Stack>

          <SwipeableDrawer
            slotProps={{
              backdrop: {
                sx: {
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                }
              }
            }}
            PaperProps={{
              sx: {
                borderRadius: '2rem 2rem 0 0',
                height: '90vh',
                backgroundColor: 'white'
              }
            }}
            anchor="bottom"
            swipeAreaWidth={0}
            open={isDrawerOpened}
            onClose={() => handleMobileClose()}
            onOpen={() => setIsDrawerOpened(true)}
          >
            <VisualsMarketingSidebar
              isMobile={isMobile}
              onClose={isMobile ? handleMobileClose : undefined}
            />
          </SwipeableDrawer>
        </Box>
      }

    </Stack>
  )
}
